import './overview.scss';
import React, { useEffect, useMemo } from 'react';
import { Row, Col, Card, Tag, Table, Typography, Space, Button } from 'antd';
import { formatAmount } from 'app/utils/amountFormatter';
import SoldeCard from '../SoldeCard';
import { RouteComponentProps } from 'react-router-dom';
import { getEcoleDashboard, IGetEcoleParam, setDates } from '../finance.reducers';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { TextFormat } from 'react-jhipster';
import { APP_DATE_FORMAT } from 'app/config/constants';
import DashBoardSkeleton from '../DashBoardSkeleton';
import { getEcole } from 'app/entities/ecole/ecole.reducer';
import BarChartCard from '../BarChartCard';
import CustomRangePicker from '../CustomRangePicker';
import DoughnutChartsSection from './DoughnutChartsSection';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const { Title } = Typography;

const OverView = (props: RouteComponentProps<{ ecoleid: string; anneeScolaireId: string }>) => {
  const { match } = props;
  const { ecoleid, anneeScolaireId } = match.params;
  const dispatch = useAppDispatch();
  const { financialDashboard, loading, startDate, endDate } = useAppSelector(state => state.finance);

  const pieDataSales = useMemo(
    () => [
      { name: 'Collecté', value: financialDashboard.totalEncaissements, currency: 'FCFA' },
      { name: 'En Attente', value: financialDashboard.totalEncaissementEnAttente, currency: 'FCFA' },
      { name: 'Espèce', value: financialDashboard.totalEncaissementEspeces, currency: 'FCFA' },
    ],
    [financialDashboard]
  );

  const pieDataInvoice = useMemo(
    () => [
      { name: 'Total Payées', value: financialDashboard.totalFacturePaye },
      { name: 'Total En Attente', value: financialDashboard.totalFactureEnAttente },
    ],
    [financialDashboard]
  );
  const pieDataSalaries = useMemo(
    () => [
      { name: 'Montant Net', value: financialDashboard.totalPaiementsSalaires, currency: 'FCFA' },
      { name: 'Frais', value: financialDashboard.totalFrais, currency: 'FCFA' },
    ],
    [financialDashboard]
  );

  const pieDataRevenue = useMemo(
    () => [
      { name: 'Payée', value: financialDashboard.totalSalairePaye },
      { name: 'Non Payée', value: financialDashboard.totalSalaireNonPaye },
    ],
    [financialDashboard]
  );

  const renderOption = (id, record, type) => {
    const url = `${
      type === 'approvisonnements' ? `/${type}/${ecoleid}/${anneeScolaireId}/${record.id}/details` : `/${type}/demande/${record.id}/details`
    }`;
    return (
      <Space size="middle">
        <Button type="primary" style={{ height: 30, width: 45 }} onClick={() => props.history.push(url)}>
          <FontAwesomeIcon icon="eye" />{' '}
        </Button>
      </Space>
    );
  };

  const approColumns = [
    { title: 'Montant', dataIndex: 'montant', key: 'montant', render: (amount: number) => `${formatAmount(amount)} FCFA` },
    {
      title: "Date d'émission",
      dataIndex: 'dateEmission',
      key: 'dateEmission',
      render: dateEmission => <TextFormat type="date" value={dateEmission} format={APP_DATE_FORMAT} />,
    },
    {
      title: 'Date de Validation',
      dataIndex: 'dateValidation',
      key: 'dateValidation',
      render: dateValidation => <TextFormat type="date" value={dateValidation} format={APP_DATE_FORMAT} />,
    },
    { title: 'Type', dataIndex: 'typeApprovisonnement', key: 'typeApprovisonnement' },
    {
      title: 'Statuts',
      dataIndex: 'statutApprovisonnement',
      key: 'statutApprovisonnement',
      align: 'center' as const,
      render: statut =>
        statut === 'ENCOURS' ? (
          <Tag color="red">En attente</Tag>
        ) : statut === 'VALIDE' ? (
          <Tag color="green">Validé</Tag>
        ) : (
          <Tag color="#dc3545">Rejeté</Tag>
        ),
    },
    {
      title: 'Details',
      dataIndex: 'id',
      key: 'id',
      align: 'center' as const,
      render: (_, record) => renderOption(_, record, 'approvisonnements'),
    },
  ];

  const virementColumns = [
    { title: 'Montant', dataIndex: 'montant', key: 'montant', render: (amount: number) => `${formatAmount(amount)} FCFA` },
    {
      title: 'Statuts',
      dataIndex: 'statut',
      key: 'statut',
      align: 'center' as const,
      render: statut =>
        statut === 'ENCOURS' ? (
          <Tag color="red">En attente</Tag>
        ) : statut === 'VALIDE' ? (
          <Tag color="green">Validé</Tag>
        ) : (
          <Tag color="#dc3545">Rejeté</Tag>
        ),
    },
    {
      title: "Date d'émission",
      dataIndex: 'dateEmission',
      key: 'dateEmission',
      render: dateEmission => <TextFormat type="date" value={dateEmission} format={APP_DATE_FORMAT} />,
    },
    {
      title: 'Details',
      dataIndex: 'id',
      key: 'id',
      align: 'center' as const,
      render: (_, record) => renderOption(_, record, 'virements'),
    },
  ];

  const soldeCardsData = [
    {
      amount: financialDashboard.soldeTotal || 0,
      name: 'Solde Total',
      title: 'Solde Total',
      className: 'card-new-customers',
    },
    {
      amount: financialDashboard.soldeScolarite || 0,
      name: 'Solde Scolarité',
      title: 'Solde compte scolarité',
      className: 'card-total-sales',
    },
    {
      amount: financialDashboard.soldeSalaire || 0,
      name: 'Solde Salaire',
      title: 'Solde compte salaire',
      className: 'card-total-revenue',
    },
    {
      amount: financialDashboard.totalFrais || 0,
      name: 'Total Frais Salaire',
      title: 'Total Frais Salaire',
      className: 'card-pending-orders',
    },
  ];

  const onChange = (_, dateString: string[]) => {
    if (dateString[0] && dateString[1]) {
      dispatch(setDates({ startDate: dateString[0], endDate: dateString[1] }));
      const entity: IGetEcoleParam = {
        ecoleId: ecoleid,
        anneeScolaireId,
        startDate: dateString[0],
        endDate: dateString[1],
      };
      dispatch(getEcoleDashboard(entity));
    }
  };

  useEffect(() => {
    dispatch(getEcoleDashboard({ ecoleId: ecoleid, anneeScolaireId, startDate, endDate }));
    dispatch(getEcole(ecoleid));
  }, [ecoleid, anneeScolaireId, startDate, endDate, dispatch]);

  return (
    <div style={{ padding: 24 }}>
      {loading || !financialDashboard ? (
        <DashBoardSkeleton />
      ) : (
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card>
              <Row gutter={[16, 16]}>
                <Col>
                  <Title level={5}>Filtrer par mois</Title>
                </Col>
                <Col span={16}>
                  <CustomRangePicker endDate={endDate} startDate={startDate} onChange={onChange} />
                </Col>
              </Row>
            </Card>
          </Col>

          {soldeCardsData.map(card => (
            <SoldeCard
              key={card.name}
              color="#ededed"
              amount={formatAmount(card.amount)}
              name={card.name}
              title={card.title}
              className={card.className}
            />
          ))}

          <Col span={24}>
            <BarChartCard monthlyFinanceDataByEcoleId={financialDashboard.monthlyFinanceDataByEcoleId} />
          </Col>

          <DoughnutChartsSection
            title="Encaissement"
            chartsData={[
              {
                title: 'Montant total encaissé',
                data: pieDataSales,
                colors: ['rgba(75,192,192,1.5)', '#bf616a', '#ffcb75'],
              },
              {
                title: 'Nombre de factures',
                data: pieDataInvoice,
                colors: ['#17a2b8', '#FF6384'],
              },
            ]}
          />

          <DoughnutChartsSection
            title="Paiement"
            chartsData={[
              {
                title: 'Montant total payé',
                data: pieDataSalaries,
                colors: ['rgba(75,192,192,1.5)', '#ffcb75'],
              },
              {
                title: 'Nombre de salaires',
                data: pieDataRevenue,
                colors: ['#17a2b8', '#FF6384'],
              },
            ]}
          />

          <Col span={24}>
            <Card title="Approvisionnement">
              {financialDashboard && <Table columns={approColumns} dataSource={financialDashboard.approvisionnements} pagination={false} />}
            </Card>
          </Col>

          <Col span={24}>
            <Card title="Virement">
              {financialDashboard && <Table columns={virementColumns} dataSource={financialDashboard.retraits} pagination={false} />}
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default OverView;
